import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { CheckIcon, CopyIcon, DownloadIcon } from 'lucide-react'

import { Button, Skeleton } from '@repo/ui'

import { SelectField } from 'components/input/fields/SelectField'
import { useSnackbar } from 'contexts'
import { useOutreachCertificate, useSetOutreachCertificate } from 'hooks/api/useDemos'
import { demosApi } from 'services/demosApi'
import { getErrorMessage } from 'utils'

import { OUTREACH_PNG } from './outreachCertificateLayout'
import { downloadOutreachPng, drawOutreachCertificate } from './renderOutreachPng'

/**
 * VRG-723 — a demo's OUTREACH CERTIFICATE: the one certificate the AE sends a prospect.
 *
 * Shown on the Demos page (a row action opens it in a modal) and in the GTM → Sales company drawer,
 * from one component so the two cannot drift (§36). Two things to do with it:
 *   - Copy link: the certificate on the demo site, on the customer app host the API built;
 *   - Download image: the PNG for the email, drawn in this browser (`renderOutreachPng.ts`).
 *
 * The preview IS the downloaded image, rendered by the same function, so what the AE sees is what
 * the prospect gets — light mode, photo padded, "Demo certificate" on the pixels.
 */
export const OutreachCertificatePanel = ({ companyId }: { companyId: string }) => {
  const { open: notify } = useSnackbar()
  const { data: view, isLoading, error } = useOutreachCertificate(companyId)
  const { mutate: change, isPending: isChanging } = useSetOutreachCertificate()
  const [preview, setPreview] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  const fetchImage = useMemo(() => (src: string) => demosApi.getOutreachImage(companyId, src), [companyId])

  useEffect(() => {
    if (!view?.certificate) {
      setPreview(null)
      return
    }
    let cancelled = false
    setPreview(null)
    setPreviewFailed(false)
    drawOutreachCertificate({ view, fetchImage })
      .then((canvas) => {
        if (!cancelled) setPreview(canvas.toDataURL('image/png'))
      })
      .catch(() => {
        if (!cancelled) setPreviewFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [view, fetchImage])

  if (isLoading) {
    return (
      <Stack spacing={4} data-testid="outreach-certificate-panel">
        <Skeleton height={40} />
        <Skeleton variant="rectangular" sx={{ width: 240, aspectRatio: `${OUTREACH_PNG.width} / ${OUTREACH_PNG.height}`, height: 'auto' }} />
        <Skeleton height={40} width="60%" />
      </Stack>
    )
  }
  if (error || !view) {
    return (
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        {error ? getErrorMessage(error) : 'Could not load the outreach certificate.'}
      </Typography>
    )
  }

  const cert = view.certificate
  const options = view.products.map((p) => ({ value: p.productId, label: p.title }))

  const onCopy = () => {
    if (!cert) return
    void navigator.clipboard.writeText(cert.url)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  const onDownload = async () => {
    setIsDownloading(true)
    try {
      await downloadOutreachPng({ view, fetchImage })
    } catch (e) {
      notify({ type: 'error', title: getErrorMessage(e) })
    } finally {
      setIsDownloading(false)
    }
  }

  const onChange = (productId: string) => {
    if (!productId || productId === cert?.productId) return
    change(
      { companyId, productId },
      {
        onSuccess: () => notify({ type: 'success', title: 'Outreach certificate changed' }),
        onError: (e) => notify({ type: 'error', title: getErrorMessage(e) }),
      },
    )
  }

  return (
    <Stack spacing={4} data-testid="outreach-certificate-panel">
      {view.canChange && options.length > 1 ? (
        <SelectField
          label="Product"
          options={options}
          value={cert?.productId ?? ''}
          onChange={(v) => onChange(String(v))}
          disabled={isChanging}
        />
      ) : (
        <Stack spacing={1}>
          <Typography variant="body3" color="text.secondary">
            Product
          </Typography>
          <Typography variant="body2">{cert?.productTitle ?? 'None'}</Typography>
        </Stack>
      )}

      {cert ? (
        <>
          <Box
            sx={{
              width: 240,
              aspectRatio: `${OUTREACH_PNG.width} / ${OUTREACH_PNG.height}`,
              borderRadius: '8px',
              overflow: 'hidden',
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            {preview ? (
              <Box component="img" src={preview} alt={`Outreach certificate for ${cert.productTitle}`} sx={{ width: '100%', height: '100%', display: 'block' }} data-testid="outreach-certificate-preview" />
            ) : previewFailed ? (
              <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 4 }}>
                <Typography variant="body3" color="text.secondary" sx={{ textAlign: 'center', lineHeight: 1.5 }}>
                  The preview could not be drawn. The download may still work.
                </Typography>
              </Stack>
            ) : (
              <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
            )}
          </Box>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, overflowWrap: 'anywhere' }} data-testid="outreach-certificate-url">
            {cert.url}
          </Typography>
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', rowGap: 3 }}>
            <Button variant="secondary" onClick={onCopy} startIcon={copied ? <CheckIcon strokeWidth={1} size={16} /> : <CopyIcon strokeWidth={1} size={16} />}>
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <Button variant="secondary" onClick={onDownload} loading={isDownloading} startIcon={<DownloadIcon strokeWidth={1} size={16} />}>
              Download image
            </Button>
          </Stack>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            The image is always light, with the photo padded and a "Demo certificate" label on it.
          </Typography>
        </>
      ) : (
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {view.canChange ? 'Pick a product to make its certificate the outreach certificate.' : 'This demo has no outreach certificate.'}
        </Typography>
      )}
    </Stack>
  )
}
